// CheckoutWithGiftCard.jsx
import { useState } from 'react';
import { Gift, Check, X, AlertCircle } from 'lucide-react';
import { summaryApi } from 'common';
import { fetchData } from 'helpers/giftCardAPI';
import { useTheme } from "../../components/Theme/ThemeProvider";

export function CheckoutWithGiftCard({ orderTotal, onGiftCardApplied }) {
  const [giftCardCode, setGiftCardCode] = useState('');
  const [appliedGiftCard, setAppliedGiftCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { colors } = useTheme();

  const validateGiftCard = async () => {
    if (!giftCardCode.trim()) {
      setError('Please enter a gift card code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const data = await fetchData(summaryApi.validateGiftCard.url, {
        method: summaryApi.validateGiftCard.method,
        body: JSON.stringify({
          code: giftCardCode,
          orderAmount: orderTotal
        })
      });

      if (data.success && data.valid) {
        setAppliedGiftCard(data.data);
        if (onGiftCardApplied) {
          onGiftCardApplied(data.data);
        }
      } else {
        setError(data.message || 'Invalid gift card code');
        setAppliedGiftCard(null);
      }
    } catch (err) {
      setError('Failed to validate gift card');
      setAppliedGiftCard(null);
    } finally {
      setLoading(false);
    }
  };

  const removeGiftCard = () => {
    setAppliedGiftCard(null);
    setGiftCardCode('');
    setError('');
    if (onGiftCardApplied) {
      onGiftCardApplied(null);
    }
  };

  return (
    <div className="rounded-lg border p-6" style={{
      backgroundColor: colors.bgSecondary,
      borderColor: colors.border
    }}>
      <div className="flex items-center mb-4">
        <Gift className="w-5 h-5 mr-2" style={{ color: colors.primary }} />
        <h3 className="text-lg font-semibold" style={{ color: colors.text }}>
          Have a Gift Card?
        </h3>
      </div>

      {!appliedGiftCard ? (
        <div>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={giftCardCode}
              onChange={(e) => setGiftCardCode(e.target.value.toUpperCase())}
              onKeyPress={(e) => e.key === 'Enter' && validateGiftCard()}
              placeholder="Enter gift card code"
              className="flex-1 px-4 py-2 rounded-lg focus:outline-none focus:ring-2"
              style={{
                backgroundColor: colors.bgPrimary,
                color: colors.text,
                border: `1px solid ${colors.border}`,
                '--tw-ring-color': colors.primary
              }}
              maxLength={20}
            />
            <button
              onClick={validateGiftCard}
              disabled={loading || !giftCardCode.trim()}
              className="px-6 py-2 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed transition"
              style={{ backgroundColor: colors.primary }}
            >
              {loading ? 'Checking...' : 'Apply'}
            </button>
          </div>
          {error && (
            <div className="flex items-start gap-2 p-3 border rounded-lg" style={{
              backgroundColor: `${colors.error}10`,
              borderColor: colors.error
            }}>
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: colors.error }} />
              <p className="text-sm" style={{ color: colors.error }}>{error}</p>
            </div>
          )}
        </div>
      ) : (
        <div className="border rounded-lg p-4" style={{
          backgroundColor: `${colors.success}10`,
          borderColor: colors.success
        }}>
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-start gap-2">
              <Check className="w-5 h-5 mt-0.5" style={{ color: colors.success }} />
              <div>
                <p className="font-medium" style={{ color: colors.text }}>Gift Card Applied</p>
                <p className="text-sm font-mono mt-1" style={{ color: colors.textSecondary }}>
                  {appliedGiftCard.code}
                </p>
              </div>
            </div>
            <button
              onClick={removeGiftCard}
              className="transition"
              style={{ color: colors.success }}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span style={{ color: colors.textSecondary }}>Gift Card Balance:</span>
              <span className="font-medium" style={{ color: colors.text }}>
                ₦{appliedGiftCard.currentBalance.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span style={{ color: colors.textSecondary }}>Applied Amount:</span>
              <span className="font-medium" style={{ color: colors.success }}>
                -₦{appliedGiftCard.applicableAmount.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between pt-2 border-t" style={{ borderColor: `${colors.success}40` }}>
              <span style={{ color: colors.textSecondary }}>Remaining Balance:</span>
              <span className="font-medium" style={{ color: colors.text }}>
                ₦{appliedGiftCard.remainingBalance.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}